import ITypedMap from '@/models/util/ITypedMap';
import { ApiHttpClient } from "@/globals";
import store from '@/store';
import { IDntSchema } from '@/models/dnt/schema/IDntSchema';
import { ITableRow } from '@vincentzhang96/dv-dnt-table-interfaces';

export type ColumnOp = '='|'=='|'eq'|'equal'|'equals'|'!='|'neq'|'notequal'
    |'<'|'lt'|'less'|'<='|'le'|'leq'|'atmost'|'>'|'gt'|'greater'|'>='|'geq'
    |'atleast'|'<=>'|'minmax'|'closed'|'<>'|'open'|'between'|'=ic'|'==ic'
    |'eqic'|'equalic'|'equalsic'|'!=ic'|'neqic'|'notequalic'|'in'|'contains'
    |'~'|'nin'|'notcontains'|'ninic'|'notcontainsic'|'!~ic';

export type UiResolvePair<T> = (keyof T) | `${(keyof T & string)}.${(keyof T & string)}`;

export interface IOptions<T> {
    uiresolve?: UiResolvePair<T>[];
    fileresolve?: (keyof T)[];
    select?: (keyof T)[];
    limit?: number;
    sort?: string;
    rowoffset?: number;
}

export interface IPagedRows<T> {
    rows: T[];
    total: number | null;
}

export interface ITableProvider {
    getTableSchema(tableName: string, region?: string): Promise<IDntSchema>;

    getTableRow<T extends ITableRow>(tableName: string, rowId: number, region?: string, opt?: IOptions<T>): Promise<T>;

    getTableRows<T extends ITableRow>(tableName: string, rowIds: number[], region?: string, opt?: IOptions<T>): Promise<ITypedMap<T>>;

    getTableRowsOffset<T extends ITableRow>(tableName: string, rowIdOffset: number, region?: string, opt?: IOptions<T>): Promise<T[]>;

    getTableRowIdsMatching(tableName: string, columnName: string, op: ColumnOp, value: string|number, region?: string): Promise<number[]>;

    getTableRowsMatching<T extends ITableRow>(tableName: string, columnName: string, op: ColumnOp, value: string|number, region?: string, opt?: IOptions<T>): Promise<T[]>;
    
    getTable<T extends ITableRow>(tableName: string, region?: string): Promise<T[]>;
    
    queryTable<T extends ITableRow>(tableName: string, query: string, region?: string, opt?: IOptions<T>): Promise<T[]>;
    
    queryTableTsv<T>(tableName: string, query: string, region?: string, opt?: IOptions<T>): Promise<string[]>;
    
    queryTableRowIds<T>(tableName: string, query: string, region?: string, opt?: IOptions<T>): Promise<number[]>;

    queryTablePaged<T extends ITableRow>(tableName: string, query: string, region?: string, opt?: IOptions<T>): Promise<IPagedRows<T>>;
}

class TableProvider implements ITableProvider {
    constructor() {
    }

    public async getTableSchema(tableName: string, region?: string): Promise<IDntSchema> {
        region = this._ensureRegion(region);

        const resp = await ApiHttpClient.get<IDntSchema>(`/server/${region}/tables/${tableName}`);
        return resp.data;
    }

    public async getTableRow<T extends ITableRow>(tableName: string, rowId: number, region?: string, opt?: IOptions<T>): Promise<T> {
        region = this._ensureRegion(region);

        const resp = await ApiHttpClient.get<T>(`/server/${region}/tables/${tableName}/${rowId}`, {
            params: {
                uiresolve: (opt && opt.uiresolve) ? opt.uiresolve.join(',') : undefined,
                fileresolve: (opt && opt.fileresolve) ? opt.fileresolve.join(',') : undefined,
                select: (opt && opt.select) ? opt.select.join(',') : undefined,
            }
        });

        return resp.data;
    }

    public async getTableRows<T extends ITableRow>(tableName: string, rowIds: number[], region?: string, opt?: IOptions<T>): Promise<ITypedMap<T>> {
        region = this._ensureRegion(region);

        const resp = await ApiHttpClient.get<ITypedMap<T>>(`/server/${region}/tables/${tableName}/${rowIds.join(',')}`, {
            params: {
                uiresolve: (opt && opt.uiresolve) ? opt.uiresolve.join(',') : undefined,
                fileresolve: (opt && opt.fileresolve) ? opt.fileresolve.join(',') : undefined,
                select: (opt && opt.select) ? opt.select.join(',') : undefined,
                limit: (opt && opt.limit) ? opt.limit : undefined,
            }
        });

        return resp.data;
    }

    public async getTableRowsOffset<T extends ITableRow>(tableName: string, rowIdOffset?: number, region?: string, opt?: IOptions<T>): Promise<T[]> {
        region = this._ensureRegion(region);

        const resp = await ApiHttpClient.get<T[]>(`/server/${region}/tables/${tableName}`, {
            params: {
                uiresolve: (opt && opt.uiresolve) ? opt.uiresolve.join(',') : undefined,
                fileresolve: (opt && opt.fileresolve) ? opt.fileresolve.join(',') : undefined,
                select: (opt && opt.select) ? opt.select.join(',') : undefined,
                limit: (opt && opt.limit) ? opt.limit : undefined,
                rowoffset: rowIdOffset,
            }
        });

        return resp.data;
    }

    public async getTableRowIdsMatching(tableName: string, columnName: string, op: ColumnOp, value: string|number, region?: string): Promise<number[]> {
        region = this._ensureRegion(region);

        const resp = await ApiHttpClient.get<number[]>(`/server/${region}/tables/${tableName}/columns/${columnName}/${op}/${value}`);

        return resp.data;
    }

    public async getTableRowsMatching<T extends ITableRow>(tableName: string, columnName: string, op: ColumnOp, value: string|number, region?: string, opt?: IOptions<T>): Promise<T[]> {
        region = this._ensureRegion(region);

        const resp = await ApiHttpClient.get<T[]>(`/server/${region}/tables/${tableName}/columns/${columnName}/${op}/${value}/data`, {
            params: {
                uiresolve: (opt && opt.uiresolve) ? opt.uiresolve.join(',') : undefined,
                fileresolve: (opt && opt.fileresolve) ? opt.fileresolve.join(',') : undefined,
                select: (opt && opt.select) ? opt.select.join(',') : undefined,
                limit: (opt && opt.limit) ? opt.limit : undefined,
            }
        });

        return resp.data;
    }
    
    public async getTable<T extends ITableRow>(tableName: string, region?: string, limit: number = 9000): Promise<T[]> {
        region = this._ensureRegion(region);

        const resp = await ApiHttpClient.get<T[]>(`/server/${region}/tables/${tableName}`, {
            params: {
                limit,
            }
        });

        return resp.data;
    }
    
    public async queryTable<T extends ITableRow>(tableName: string, query: string, region?: string, opt?: IOptions<T>): Promise<T[]> {
        region = this._ensureRegion(region);
        
        const resp = await ApiHttpClient.get<T[]>(`/server/${region}/tables/${tableName}/query/data`, {
            params: {
                q: query,
                limit: (opt && opt.limit) ? opt.limit : undefined,
                uiresolve: (opt && opt.uiresolve) ? opt.uiresolve.join(',') : undefined,
                fileresolve: (opt && opt.fileresolve) ? opt.fileresolve.join(',') : undefined,
                select: (opt && opt.select) ? opt.select.join(',') : undefined,
            }
        });

        return resp.data;
    }
    
    public async queryTableTsv<T>(tableName: string, query: string, region?: string, opt?: IOptions<T>): Promise<string[]> {
        region = this._ensureRegion(region);
        
        const resp = await ApiHttpClient.get<string>(`/server/${region}/tables/${tableName}/query/data`, {
            params: {
                q: query,
                limit: (opt && opt.limit) ? opt.limit : undefined,
                uiresolve: (opt && opt.uiresolve) ? opt.uiresolve.join(',') : undefined,
                fileresolve: (opt && opt.fileresolve) ? opt.fileresolve.join(',') : undefined,
                select: (opt && opt.select) ? opt.select.join(',') : undefined,
            }
        });

        return resp.data.split('\n');
    }
    
    public async queryTableRowIds<T>(tableName: string, query: string, region?: string, opt?: IOptions<T>): Promise<number[]> {
        region = this._ensureRegion(region);

        const resp = await ApiHttpClient.get<number[]>(`/server/${region}/tables/${tableName}/query`, {
            params: {
                q: query,
                limit: (opt && opt.limit) ? opt.limit : undefined,
                uiresolve: (opt && opt.uiresolve) ? opt.uiresolve.join(',') : undefined,
                fileresolve: (opt && opt.fileresolve) ? opt.fileresolve.join(',') : undefined,
                select: (opt && opt.select) ? opt.select.join(',') : undefined,
            }
        });

        return resp.data;
    }

    public async queryTablePaged<T extends ITableRow>(tableName: string, query: string, region?: string, opt?: IOptions<T>): Promise<IPagedRows<T>> {
        region = this._ensureRegion(region);

        const resp = await ApiHttpClient.get<T[]>(`/server/${region}/tables/${tableName}/query/data`, {
            params: {
                q: query,
                limit: (opt && opt.limit) ? opt.limit : undefined,
                rowoffset: (opt && opt.rowoffset !== undefined) ? opt.rowoffset : undefined,
                sort: (opt && opt.sort) ? opt.sort : undefined,
                uiresolve: (opt && opt.uiresolve) ? opt.uiresolve.join(',') : undefined,
                fileresolve: (opt && opt.fileresolve) ? opt.fileresolve.join(',') : undefined,
                select: (opt && opt.select) ? opt.select.join(',') : undefined,
            },
        });

        const raw = resp.headers ? resp.headers['x-total-count'] : undefined;
        const parsed = raw !== undefined && raw !== null ? Number(raw) : NaN;
        return { rows: resp.data, total: Number.isFinite(parsed) ? parsed : null };
    }

    private _ensureRegion(region?: string): string {
        if (!region) {
            return store.state.regionCode;
        }

        return region;
    }
}

export default new TableProvider();
